// stylushandler.ts ↔ game/stylushandler.cpp (SimUIForm::StylusHandler — the
// MOUSE-MODE playfield UI; the shipped WEB client runs it: game.cpp:296-301
// forces gfMouseMode under #if defined(WEB), and SimUI.cpp:171/219-232 pick
// kuitStylus whenever gfLassoSelection || gfMouseMode). Left-drag rubber-bands
// the drag-select rect (live-applied via Simulation::SetSelection), right-drag
// pans the map, pen-hold pops the unit menu. PLUS the stylus-only callees the
// FingerHandler port left behind:
//   - Simulation.cpp:653-684 Simulation::SetSelection (the live rect-select
//     sim mutator over gwrcSelection)
//   - SimUI.cpp:1261-1269 AddPointToLassoSelection + SimUI.cpp:2552-2567
//     PtInPolygon (the lasso arms; gfLassoSelection defaults false on web ->
//     the rubber-band BOX is the live path)
//   - misc.cpp:389-405 WRect::Set (the two-WPoint normalizing Set)
// The C++ drag statics (SimUI.cpp:42-47 gfDragSelecting/s_wptSelect1/2/
// gwrcSelection/s_awptSelection/s_cwptSelection/gfLassoSelection) live in the
// injected DragSelectionState (ui/simuiform.ts) — the HUD form draws the rect
// (SimUI.cpp:1477-1501 OnPaint) and invalidates it (InvalidateDragSelection).

import type { Event } from './event.ts';
import {
  penDownEvent,
  penUpEvent,
  penMoveEvent,
  penHoldEvent,
  kfEvtRightButton,
  kfRedrawDirty,
  kfRedrawBeforeTimer,
} from './event.ts';
import { Vec2d } from './dragrect.ts';
import type { SimUIForm as SimUIFormInput } from './fingerhandler.ts';
import {
  kfMasSelect,
  kfMasMove,
  kfMasAttack,
  kfMasShowMenu,
  kwcScrollBorderSize,
  kwcScrollStepPerTick,
} from './fingerhandler.ts';
import type { DragSelectionState } from './simuiform.ts';
import type { RectLike } from './rect.ts';
import { kfGobActive, kfGobUnit, kfGobStructure } from '../../../sim/src/gob/gob.ts';
import type { UnitGob } from '../../../sim/src/gob/unitgob.ts';
import type { SimContext } from '../../../sim/src/context.ts';
import { kumTowers } from '../../../sim/src/player/masks.ts';
import { WcFromTile16ths } from '../../../sim/src/math/coords.ts';
import type { WPoint } from '../../../sim/src/types.ts';

// game/stylushandler.cpp:116 (TUNE)
const kwcMinSelection = WcFromTile16ths(8);

// game/misc.cpp:389-405 WRect::Set(WPoint, WPoint) — normalizing (min/max) set;
// gwrcSelection is a WRect and every stylus site goes through this overload.
export function WRectSet(wrc: RectLike, wpt1: WPoint, wpt2: WPoint): void {
  if (wpt1.wx < wpt2.wx) {
    wrc.left = wpt1.wx;
    wrc.right = wpt2.wx;
  } else {
    wrc.left = wpt2.wx;
    wrc.right = wpt1.wx;
  }
  if (wpt1.wy < wpt2.wy) {
    wrc.top = wpt1.wy;
    wrc.bottom = wpt2.wy;
  } else {
    wrc.top = wpt2.wy;
    wrc.bottom = wpt1.wy;
  }
}

// game/SimUI.cpp:2552-2567 PtInPolygon — the lasso containment test (crossing
// count over the world-coord point list).
export function PtInPolygon(awpt: WPoint[], cwpt: number, wx: number, wy: number): boolean {
  let fInside = false;
  let j = cwpt - 1;
  for (let i = 0; i < cwpt; i++) {
    const pwptI = awpt[i];
    const pwptJ = awpt[j];
    if (
      ((pwptI.wy <= wy && wy < pwptJ.wy) || (pwptJ.wy <= wy && wy < pwptI.wy)) &&
      wx < Math.trunc(((pwptJ.wx - pwptI.wx) * (wy - pwptI.wy)) / (pwptJ.wy - pwptI.wy)) + pwptI.wx
    ) {
      fInside = !fInside;
    }
    j = i;
  }
  return fInside;
}

// game/SimUI.cpp:1261-1269 AddPointToLassoSelection — append when the new point
// moved >= 4 tile-16ths from the last one (capacity 250, C++ keeps the last slot
// free).
export function AddPointToLassoSelection(drag: DragSelectionState, wpt: WPoint): void {
  const last = drag.awptSelection[drag.cwptSelection - 1];
  if (
    Math.abs(last.wx - wpt.wx) >= WcFromTile16ths(4) ||
    Math.abs(last.wy - wpt.wy) >= WcFromTile16ths(4)
  ) {
    drag.awptSelection[drag.cwptSelection] = { wx: wpt.wx, wy: wpt.wy };
    if (drag.cwptSelection < 250 - 1) {
      drag.cwptSelection++;
    }
  }
}

// game/Simulation.cpp:653-684 Simulation::SetSelection — the LIVE rect-select
// apply: every unit gob gets Select(fSelect) from the world-coord selection
// rect (towers are the only selectable structures; god mode widens ownership).
export function SimulationSetSelection(
  ctx: SimContext,
  drag: DragSelectionState,
  prc: RectLike,
): void {
  if (drag.fLassoSelection || !drag.fDragSelecting) {
    return;
  }

  for (let pgobT = ctx.gobm.GetFirstGob(); pgobT !== null; pgobT = ctx.gobm.GetNextGob(pgobT)) {
    const ff = pgobT.GetFlags();

    // If were in select mode then only Gobs inside the selection rectangle
    // get to be selected.

    if ((ff & kfGobUnit) === 0) {
      continue;
    }
    let fSelect = false;
    const punt = pgobT as UnitGob;
    if (
      (ctx.fGodMode || punt.GetOwner() === ctx.plrLocal) &&
      (ff & (kfGobActive | kfGobUnit)) === (kfGobActive | kfGobUnit)
    ) {
      if ((ff & kfGobStructure) === 0 || (punt.GetConsts().um & kumTowers) !== 0) {
        const wptGobCenter: WPoint = { wx: 0, wy: 0 };
        punt.GetCenter(wptGobCenter);
        if (
          wptGobCenter.wx >= prc.left &&
          wptGobCenter.wx < prc.right &&
          wptGobCenter.wy >= prc.top &&
          wptGobCenter.wy < prc.bottom
        ) {
          // Rect::PtIn
          fSelect = true;
        }
      }
    }
    punt.Select(fSelect);
  }
}

/** The injected reaches beyond the input slice (the C++ m_psui is the ONE
 *  SimUIForm; the TS HUD half owns the drag state + the update-map
 *  invalidation). */
export interface StylusDeps {
  sui: SimUIFormInput;
  /** the SimUI.cpp:42-47 drag-select statics (the HUD form draws from them). */
  drag: DragSelectionState;
  /** m_psui->InvalidateDragSelection() (SimUI.cpp:1398-1446). */
  invalidateDragSelection: () => void;
}

// ht.h:3792-3813 (SimUIForm::StylusHandler) + game/stylushandler.cpp
export class StylusHandler {
  private m_psui: SimUIFormInput;
  private drag: DragSelectionState;
  private invalidateDragSelection: () => void;
  private m_fDragging: boolean;
  private m_fMapDragging: boolean;
  private m_xDrag = 0;
  private m_yDrag = 0;
  private m_wxViewDrag = 0;
  private m_wyViewDrag = 0;

  // game/stylushandler.cpp:5-10
  constructor(deps: StylusDeps) {
    this.m_psui = deps.sui;
    this.drag = deps.drag;
    this.invalidateDragSelection = deps.invalidateDragSelection;
    this.m_fDragging = false;
    this.m_fMapDragging = false;
  }

  // ht.h:3796-3799 (the inline PenHandler virtuals: OnPaint empty, flags 0)
  OnPaint(_pbm: unknown): void {}
  GetFlags(): number {
    return 0;
  }
  SetFlags(_ff: number): void {}
  Dispose(): void {} // trivial dtor

  // game/stylushandler.cpp:12-71
  OnPenEvent(pevt: Event, fScrollOnly: boolean): boolean {
    // If the right mouse button is pressed handle map dragging.

    if (pevt.eType === penMoveEvent || pevt.eType === penDownEvent || pevt.eType === penUpEvent) {
      if (pevt.ff & kfEvtRightButton) {
        if (pevt.eType === penDownEvent) {
          this.EnterMapDrag(pevt);
        } else if (pevt.eType === penMoveEvent && this.m_fMapDragging) {
          this.UpdateMapDrag(pevt);
        } else {
          this.m_fMapDragging = false;
        }
        return true;
      }
      this.m_fMapDragging = false;
    }

    // Can't scroll the playfield from this PenHandler

    if (fScrollOnly) {
      return true;
    }

    // Dragging mode captures all pen events except pen-hold for the duration of
    // the drag to perform drag-scrolling, etc (shift-drag or graffiti-scroll)

    if (this.m_fDragging && pevt.eType !== penHoldEvent) {
      this.OnPenDrag(pevt);

      // Force draw to keep dragging as interactive as possible
      // Raises redraw priority above next update

      this.m_psui.evm.SetRedrawFlags(kfRedrawDirty | kfRedrawBeforeTimer);
      return true;
    }

    switch (pevt.eType) {
      case penHoldEvent:
        // Popup Gob menu if pen held on one of them
        this.OnPenHold(pevt);
        break;

      case penDownEvent:
        // Handle the beginning of a drag selection operation or a tap on a
        // unit/structure
        this.OnPenDown(pevt);
        break;

      case penUpEvent:
        // Handle the canceling of any in progress modes
        this.OnPenUp(pevt);
        break;
    }

    return true;
  }

  // game/stylushandler.cpp:73-108
  private OnPenDown(pevt: Event): void {
    // Enter dragging mode; this'll cause OnPenDrag to be called until
    // that mode exits.

    this.m_fDragging = true;

    // Calc a hit target in world coords

    const c = this.m_psui.gctx.coords;
    const wxPen = c.WcFromPc(pevt.x);
    const wyPen = c.WcFromPc(pevt.y);
    const wptView: WPoint = { wx: 0, wy: 0 };
    this.m_psui.GetViewPos(wptView); // gsim.GetViewPos
    const wxTarget = wptView.wx + wxPen;
    const wyTarget = wptView.wy + wyPen;

    // s_wptSelect1 & 2 are used to determine the transition from a
    // tentative selection to a real one and must contain the beginning
    // and current pen positions, respectively.

    this.drag.wptSelect1.wx = wxTarget;
    this.drag.wptSelect1.wy = wyTarget;
    this.drag.wptSelect2.wx = this.drag.wptSelect1.wx;
    this.drag.wptSelect2.wy = this.drag.wptSelect1.wy;

    if (this.drag.fLassoSelection) {
      this.drag.awptSelection[0] = { wx: this.drag.wptSelect1.wx, wy: this.drag.wptSelect1.wy };
      this.drag.cwptSelection = 1;
    } else {
      WRectSet(this.drag.wrcSelection, this.drag.wptSelect1, this.drag.wptSelect2);
      SimulationSetSelection(this.m_psui.ctx, this.drag, this.drag.wrcSelection);
    }

    // Only Select. Leave moving and attacking to the pen up handler.

    this.m_psui.MoveOrAttackOrSelectAt(pevt.x, pevt.y, kfMasSelect);
  }

  // game/stylushandler.cpp:110-113
  private OnPenUp(_pevt: Event): void {
    this.CancelModes();
  }

  // game/stylushandler.cpp:118-221
  private OnPenDrag(pevt: Event): void {
    const c = this.m_psui.gctx.coords;
    const wptView: WPoint = { wx: 0, wy: 0 };
    this.m_psui.GetViewPos(wptView);
    const wxView = wptView.wx;
    const wyView = wptView.wy;

    switch (pevt.eType) {
      case penUpEvent:
        // If this is a forced up event, cancel our modes

        if (pevt.dw === 1) {
          this.CancelModes();
          break;
        }

        // Real pen event. Leaving drag mode.

        this.m_fDragging = false;

        // If we're drag selecting complete the process. Otherwise handle this
        // event as a tap on an enemy (attack it) or location (move to it)

        if (this.drag.fDragSelecting) {
          // Invalidate this area first so we get it to redraw

          this.invalidateDragSelection();

          // Turn off selection

          this.drag.fDragSelecting = false;

          if (this.drag.fLassoSelection && this.drag.cwptSelection > 1) {
            const ctx = this.m_psui.ctx;
            for (
              let pgobT = ctx.gobm.GetFirstGob();
              pgobT !== null;
              pgobT = ctx.gobm.GetNextGob(pgobT)
            ) {
              const ff = pgobT.GetFlags();

              // If were in select mode then only Gobs inside the
              // selection rectangle get to be selected.

              let fSelect = false;
              if (
                (ctx.fGodMode || pgobT.GetOwner() === ctx.plrLocal) &&
                (ff & (kfGobActive | kfGobUnit)) === (kfGobActive | kfGobUnit)
              ) {
                const wptGobCenter: WPoint = { wx: 0, wy: 0 };
                pgobT.GetCenter(wptGobCenter);
                if (
                  PtInPolygon(
                    this.drag.awptSelection,
                    this.drag.cwptSelection,
                    wptGobCenter.wx,
                    wptGobCenter.wy,
                  )
                ) {
                  fSelect = true;
                }
              }
              if (ff & kfGobUnit) {
                (pgobT as UnitGob).Select(fSelect);
              }
            }

            this.drag.cwptSelection = 0;
          }
        } else {
          this.m_psui.MoveOrAttackOrSelectAt(
            pevt.x,
            pevt.y,
            kfMasMove | kfMasAttack | kfMasShowMenu,
          );
        }
        break;

      case penMoveEvent:
        {
          if (this.drag.fDragSelecting) {
            // Inval old pos of selection rect

            this.invalidateDragSelection();
          }

          // Update() code uses s_wptSelect2 to determine if edge scrolling
          // is necessary

          this.drag.wptSelect2.wx = wxView + c.WcFromPc(pevt.x);
          this.drag.wptSelect2.wy = wyView + c.WcFromPc(pevt.y);
          WRectSet(this.drag.wrcSelection, this.drag.wptSelect1, this.drag.wptSelect2);

          if (!this.drag.fDragSelecting) {
            if (
              this.drag.wrcSelection.right - this.drag.wrcSelection.left >= kwcMinSelection ||
              this.drag.wrcSelection.bottom - this.drag.wrcSelection.top >= kwcMinSelection
            ) {
              this.drag.fDragSelecting = true;
            }
          }

          if (this.drag.fDragSelecting) {
            // Inval new size of selection rect

            this.invalidateDragSelection();

            if (this.drag.fLassoSelection) {
              AddPointToLassoSelection(this.drag, this.drag.wptSelect2);
            }

            SimulationSetSelection(this.m_psui.ctx, this.drag, this.drag.wrcSelection);
          }
        }
        break;
    }
  }

  // game/stylushandler.cpp:223-247
  private OnPenHold(pevt: Event): void {
    const c = this.m_psui.gctx.coords;
    const wptView: WPoint = { wx: 0, wy: 0 };
    this.m_psui.GetViewPos(wptView);
    const wxTarget = wptView.wx + c.WcFromPc(pevt.x);
    const wyTarget = wptView.wy + c.WcFromPc(pevt.y);

    const pgobHit = this.m_psui.HitTest(wxTarget, wyTarget, kfGobActive | kfGobUnit);

    // TS-only: a mouse press-and-HOLD arms attack-move (the desktop analog of the touch
    // tap-hold) when own units are selected — over empty ground, or onto an enemy target.
    // Keep m_fDragging set so the pen-up still issues the order via OnPenDrag, which the
    // central armed check (MoveOrAttackOrSelect) turns into the attack-move.
    if (
      this.m_psui.HasSelectedUnits() &&
      (pgobHit === null || !this.m_psui.IsSelectionCommand(pgobHit))
    ) {
      this.m_psui.SetAttackMoveArmed(true);
      return;
    }

    if (pgobHit === null) {
      return;
    }

    // Don't popup menu if the player doesn't own the Gob (unless in God mode)
    // God mode is limited for multiplayer games.

    const ctx = this.m_psui.ctx;
    if ((!ctx.fGodMode || ctx.fMultiplayer) && pgobHit.GetSide() !== ctx.plrLocal!.GetSide()) {
      return;
    }

    this.m_fDragging = false;
    this.m_psui.host.PopupMenu(pgobHit); // pgobHit->PopupMenu()
  }

  // game/stylushandler.cpp:249-255
  private EnterMapDrag(pevt: Event): void {
    this.m_fMapDragging = true;
    this.m_xDrag = pevt.x;
    this.m_yDrag = pevt.y;
    const wptView: WPoint = { wx: 0, wy: 0 };
    this.m_psui.GetViewPos(wptView);
    this.m_wxViewDrag = wptView.wx;
    this.m_wyViewDrag = wptView.wy;
  }

  // game/stylushandler.cpp:257-262
  private UpdateMapDrag(pevt: Event): void {
    const c = this.m_psui.gctx.coords;
    let v = new Vec2d(this.m_xDrag - pevt.x, this.m_yDrag - pevt.y);
    v = v.scale(this.m_psui.gnScrollSpeed);
    // WcFromPc takes the int pixel delta (the C++ passes Vec2d's int members)
    this.m_psui.SetViewPos(
      this.m_wxViewDrag + c.WcFromPc(Math.trunc(v.dx)),
      this.m_wyViewDrag + c.WcFromPc(Math.trunc(v.dy)),
    );
  }

  // game/stylushandler.cpp:264-274
  private CancelModes(): void {
    this.m_fDragging = false;

    if (this.drag.fDragSelecting) {
      // Invalidate this area first so we get it to redraw

      this.invalidateDragSelection();
      this.drag.fDragSelecting = false;
    }
  }

  // game/stylushandler.cpp:276-321 — n = ticks since the last poll (polled per
  // tick now; the step scales by n for a smooth, constant edge-scroll speed)
  CheckScroll(n = 1): void {
    // If the player is rectangle selecting and at the screen edge scroll the
    // screen.

    if (!this.drag.fDragSelecting) {
      return;
    }

    const wcStep = Math.max(1, kwcScrollStepPerTick * n);
    const c = this.m_psui.gctx.coords;
    const sizPlayfield = this.m_psui.GetPlayfieldSize(); // ggame.GetPlayfieldSize
    const wcxPlayfield = c.WcFromUpc(sizPlayfield.cx);
    const wcyPlayfield = c.WcFromUpc(sizPlayfield.cy);

    const wptView: WPoint = { wx: 0, wy: 0 };
    this.m_psui.GetViewPos(wptView);
    const wxView = wptView.wx;
    const wyView = wptView.wy;

    let wxViewNew = wxView;
    let wyViewNew = wyView;

    if (this.drag.wptSelect2.wx < wxView + kwcScrollBorderSize) {
      wxViewNew -= wcStep;
    } else if (this.drag.wptSelect2.wx > wxView + wcxPlayfield - kwcScrollBorderSize) {
      wxViewNew += wcStep;
    }

    if (this.drag.wptSelect2.wy < wyView + kwcScrollBorderSize) {
      wyViewNew -= wcStep;
    } else if (this.drag.wptSelect2.wy > wyView + wcyPlayfield - kwcScrollBorderSize) {
      wyViewNew += wcStep;
    }

    if (wxViewNew !== wxView || wyViewNew !== wyView) {
      this.m_psui.SetViewPos(wxViewNew, wyViewNew);
      const wptActual: WPoint = { wx: 0, wy: 0 };
      this.m_psui.GetViewPos(wptActual);
      this.drag.wptSelect2.wx += wptActual.wx - wxView;
      this.drag.wptSelect2.wy += wptActual.wy - wyView;
      WRectSet(this.drag.wrcSelection, this.drag.wptSelect1, this.drag.wptSelect2);

      if (this.drag.fLassoSelection) {
        AddPointToLassoSelection(this.drag, this.drag.wptSelect2);
      }

      SimulationSetSelection(this.m_psui.ctx, this.drag, this.drag.wrcSelection);
    }
  }
}
